import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, BadgeCheck, KeyRound, LibraryBig, RadioTower, UserRound } from "lucide-react";
import { BackCoverFooter, PageShell, Section, TopNav, brandPalette } from "./components/brand-scaffold";
import { FaqSection, LinkClusterSection } from "./components/seo-support";
import {
  FALLBACK_HOTLINES,
  FALLBACK_TEMPLATE_BUNDLES,
  buildMarketplacePath,
  buildResponderPath,
  normalizeHotline,
  requestHotlineCatalog,
  requestHotlineDetail,
  requestHotlineTemplateBundle,
  slugifyMarketplaceSegment,
  uniqueResponders,
  type MarketplaceHotline,
  type MarketplaceResponder,
  type MarketplaceTemplateBundle
} from "./marketplace-data";
import { ROUTE_FAQS } from "./seo-content";
import { siteHeaderActions, siteNavItems } from "./content";

type RouteState =
  | { kind: "index" }
  | { kind: "responder"; responderSlug: string }
  | { kind: "hotline"; responderSlug: string; hotlineSlug: string };

function localizeStatus(value: string | null | undefined) {
  const key = String(value || "").toLowerCase();
  const mapping: Record<string, string> = {
    healthy: "正常",
    degraded: "降级",
    offline: "离线",
    approved: "已通过",
    pending: "待审核",
    rejected: "已拒绝",
    public: "公开",
    enabled: "已启用",
    disabled: "已停用",
    demo: "示例"
  };
  return mapping[key] || String(value || "未知");
}

function localizeCapability(value: string) {
  const mapping: Record<string, string> = {
    classification: "分类",
    "routing.assist": "路由辅助",
    "repo.summarize": "仓库摘要",
    "code.explain": "代码解释",
    "ops.audit": "运维审计",
    "trace.review": "轨迹审查"
  };
  return mapping[value] || value;
}

function localizeTaskType(value: string) {
  const mapping: Record<string, string> = {
    text_classify: "文本分类",
    text_classification: "文本分类",
    repo_summary: "仓库摘要",
    audit_review: "审计复核"
  };
  return mapping[value] || value;
}

function formatUtcDate(value: string | null | undefined) {
  if (!value) {
    return "未提供";
  }
  return value.replace("T", " ").replace("Z", " UTC").slice(0, 20);
}

function formatExecMs(value: number | null | undefined) {
  if (typeof value !== "number") {
    return "未提供";
  }
  return `${value} ms`;
}

function summarizePublicKey(value: string | null | undefined) {
  if (!value) {
    return "未提供";
  }
  const compact = value.replace(/-----BEGIN PUBLIC KEY-----|-----END PUBLIC KEY-----|\s+/g, "");
  return `已提供 (${compact.slice(0, 12)}…)`;
}

function jsonPreview(value: unknown) {
  if (!value) {
    return "未提供";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function parseMarketplaceRoute(pathname: string): RouteState {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (parts.length <= 1) {
    return { kind: "index" };
  }
  if (parts[1] !== "responders") {
    return { kind: "index" };
  }
  if (parts.length === 3) {
    return { kind: "responder", responderSlug: parts[2] };
  }
  if (parts.length >= 4) {
    return { kind: "hotline", responderSlug: parts[2], hotlineSlug: parts[3] };
  }
  return { kind: "index" };
}

function HotlineListView({
  items,
  responders,
  loadingState
}: {
  items: MarketplaceHotline[];
  responders: MarketplaceResponder[];
  loadingState: "loading" | "ready" | "fallback";
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTaskType, setActiveTaskType] = useState<string>("all");
  const [activeCapability, setActiveCapability] = useState<string>("all");
  const isFallbackPreview = loadingState !== "ready";

  const taskTypes = useMemo(() => ["all", ...Array.from(new Set(items.flatMap((item) => item.task_types || []))).slice(0, 8)], [items]);
  const capabilities = useMemo(
    () => ["all", ...Array.from(new Set(items.flatMap((item) => item.capabilities || []))).slice(0, 8)],
    [items]
  );

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      const matchesTerm =
        !term ||
        JSON.stringify([item.display_name, item.hotline_id, item.responder_id, ...(item.capabilities || []), ...(item.task_types || [])])
          .toLowerCase()
          .includes(term);
      const matchesTaskType = activeTaskType === "all" || (item.task_types || []).includes(activeTaskType);
      const matchesCapability = activeCapability === "all" || (item.capabilities || []).includes(activeCapability);
      return matchesTerm && matchesTaskType && matchesCapability;
    });
  }, [activeCapability, activeTaskType, items, searchTerm]);

  return (
    <section id="marketplace-list" className="scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8 md:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {isFallbackPreview ? (
            <div className="inline-flex items-center gap-2 border-4 border-black bg-white/85 px-4 py-3 text-sm font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
              示例 Hotline — Preview catalog 中的示例 Responder，先按 <a className="underline" href="/docs/quick-start-caller/">Caller Quick Start</a> 跑通第一次调用
            </div>
          ) : null}
          <div className="inline-flex items-center gap-2 border-4 border-black bg-[#F8F6EF] px-4 py-3 text-sm font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
            当前页面仅按 protocol 目录字段展示
          </div>
        </div>

        <div className="mb-8 grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <article className="border-4 border-black bg-white/80 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-zinc-500">目录筛选</p>
            <div className="mt-4 grid gap-4">
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="按 hotline、responder_id、能力、任务类型搜索..."
                className="w-full border-4 border-black bg-[#F8F6EF] px-4 py-4 text-sm font-bold outline-none"
              />
              <div className="grid gap-3 lg:grid-cols-2">
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-zinc-500">任务类型</p>
                  <div className="flex flex-wrap gap-2">
                    {taskTypes.map((taskType) => (
                      <button
                        key={taskType}
                        type="button"
                        onClick={() => setActiveTaskType(taskType)}
                        className="border-2 border-black px-3 py-2 text-[11px] font-black uppercase"
                        style={{
                          backgroundColor: activeTaskType === taskType ? brandPalette.blue : "#ffffff",
                          color: activeTaskType === taskType ? "#ffffff" : "#111111"
                        }}
                      >
                        {taskType === "all" ? "全部" : localizeTaskType(taskType)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-[0.2em] text-zinc-500">能力标签</p>
                  <div className="flex flex-wrap gap-2">
                    {capabilities.map((capability) => (
                      <button
                        key={capability}
                        type="button"
                        onClick={() => setActiveCapability(capability)}
                        className="border-2 border-black px-3 py-2 text-[11px] font-black uppercase"
                        style={{
                          backgroundColor: activeCapability === capability ? brandPalette.purple : "#ffffff",
                          color: activeCapability === capability ? "#ffffff" : "#111111"
                        }}
                      >
                        {capability === "all" ? "全部" : localizeCapability(capability)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm font-bold text-zinc-700">
                当前可见 {filteredItems.length} 条 hotline · {responders.length} 个 responder
              </p>
            </div>
          </article>

          <article className="border-4 border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#A3E635]">Responder 索引</p>
            <div className="mt-5 grid gap-3">
              {responders.slice(0, 4).map((responder) => (
                <a
                  key={responder.responder_id}
                  href={buildResponderPath(responder)}
                  className="border-2 border-white/30 bg-white/10 p-4 transition-colors hover:bg-white/20"
                >
                  <p className="text-sm font-black uppercase">{responder.responder_id}</p>
                  <p className="mt-2 text-xs font-bold text-zinc-200">
                    {responder.hotline_count} 条 hotline · {responder.capabilities.slice(0, 2).map(localizeCapability).join(" · ")}
                  </p>
                </a>
              ))}
            </div>
          </article>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {filteredItems.length === 0 ? (
            <article className="border-4 border-dashed border-black bg-white/70 p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] lg:col-span-2">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-zinc-500">无结果</p>
              <h2 className="mt-2 text-3xl font-black uppercase">
                {items.length === 0 ? "公开目录暂时没有已上线 hotline" : "当前筛选条件下没有可显示的 hotline"}
              </h2>
              <p className="mt-4 text-sm font-bold leading-6 text-zinc-800">
                {items.length === 0
                  ? "这不是错误：首批真实 hotline 上线前，目录会保持空态。想先验证调用链路，请走 Caller Quick Start。"
                  : "放宽筛选条件后再查看完整目录。"}
              </p>
              {items.length === 0 ? (
                <a
                  href="/docs/quick-start-caller/"
                  className="mt-5 inline-flex border-4 border-black bg-[#A3E635] px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                >
                  跑通第一次调用
                </a>
              ) : null}
            </article>
          ) : null}

          {filteredItems.map((item, index) => (
            <a
              key={item.hotline_id}
              href={buildMarketplacePath(item)}
              className="relative border-4 border-black bg-white/80 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] font-black uppercase bg-[#FACC15] text-black border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                Preview
              </span>
              <div className="flex">
                <div
                  className="flex w-24 items-center justify-center border-r-4 border-black text-3xl font-black"
                  style={{ backgroundColor: index % 3 === 0 ? brandPalette.green : index % 3 === 1 ? brandPalette.blue : brandPalette.orange }}
                >
                  {String(index + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.25em] text-zinc-500">{item.responder_id}</p>
                      <h2 className="mt-1 text-2xl font-black uppercase">{item.display_name}</h2>
                      {isFallbackPreview ? (
                        <p className="mt-2 text-xs font-black uppercase tracking-[0.18em] text-[#B45309]">Preview 示例，不可直接调用</p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="border-2 border-black bg-white px-3 py-2 text-xs font-black uppercase">{localizeStatus(item.status)}</span>
                      <span className="border-2 border-black bg-[#FEF3C7] px-3 py-2 text-xs font-black uppercase">
                        {localizeStatus(item.availability_status)}
                      </span>
                    </div>
                  </div>
                  {item.summary ? (
                    <p className="mt-3 text-sm font-bold leading-6 text-zinc-700">{item.summary}</p>
                  ) : null}
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="border-2 border-black bg-[#F8F6EF] px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">任务类型</p>
                      <p className="mt-2 text-xs font-black leading-5 text-zinc-800">
                        {(item.task_types || []).map(localizeTaskType).join(" · ") || "未提供"}
                      </p>
                    </div>
                    <div className="border-2 border-black bg-[#F8F6EF] px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">能力</p>
                      <p className="mt-2 text-xs font-black leading-5 text-zinc-800">
                        {(item.capabilities || []).map(localizeCapability).join(" · ") || "未提供"}
                      </p>
                    </div>
                  </div>
                  {(item.input_attachments?.accepts_files || item.output_attachments?.includes_files) ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.input_attachments?.accepts_files ? (
                        <span className="border-2 border-black bg-[#DBEAFE] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em]">接受文件输入</span>
                      ) : null}
                      {item.output_attachments?.includes_files ? (
                        <span className="border-2 border-black bg-[#F3E8FF] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em]">可返回文件</span>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="mt-5 flex items-center gap-2 text-sm font-black uppercase text-black">
                    打开 hotline 详情
                    <ArrowRight className="h-4 w-4" strokeWidth={3} />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>

        <LinkClusterSection
          eyebrow="Marketplace Paths"
          title="从 Marketplace 往下走"
          intro="目录页解决的是发现问题。确定了方向后，下一步一般会进入下面几个入口。"
          accent={brandPalette.orange}
          links={[
            {
              href: "/docs/quick-start-caller/",
              title: "Caller Quick Start",
              summary: "如果你是能力调用方，这里会带你把 supervisor、本地 catalog 和第一次 hotline call 跑通。",
              meta: "caller · local mode",
            },
            {
              href: "/docs/quick-start-responder/",
              title: "Responder Quick Start",
              summary: "如果你想把自己的模型或工作流挂成 Hotline，这里是最短路径。",
              meta: "responder · publish hotline",
            },
            {
              href: "/docs/",
              title: "Docs",
              summary: "继续查协议字段、Platform API、CLI 和自托管部署细节。",
              meta: "schema · api · deploy",
            },
            {
              href: "/console/",
              title: "Console",
              summary: "查看审批、路由、状态和运行时控制面的原型，理解 Marketplace 之外的控制链路。",
              meta: "approval · runtime · routing",
            },
          ]}
        />

        <FaqSection
          eyebrow="Marketplace FAQ"
          title="Marketplace 常见问题"
          intro="这些问题对应了目录页最常见的搜索意图，也会同步进入这一页的 FAQ 结构化数据。"
          items={ROUTE_FAQS["/marketplace/"]}
          accent={brandPalette.orange}
        />
      </div>
    </section>
  );
}

function ResponderView({ responder }: { responder: MarketplaceResponder }) {
  return (
    <section id="marketplace-responder" className="scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8 md:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6">
          <a
            href="/marketplace"
            className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={3} />
            返回 Marketplace
          </a>
        </div>

        <div className="grid gap-6 xl:grid-cols-[0.72fr_1.28fr]">
          <aside className="xl:sticky xl:top-32 xl:self-start">
            <article className="border-4 border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex h-16 w-16 items-center justify-center border-4 border-black bg-[#F97316] text-black">
                <UserRound className="h-8 w-8" strokeWidth={3} />
              </div>
              <p className="mt-5 text-xs font-black uppercase tracking-[0.24em] text-[#F97316]">Responder</p>
              <h2 className="mt-3 break-all text-3xl font-black uppercase">{responder.responder_id}</h2>
              <p className="mt-4 text-sm font-bold leading-6 text-zinc-200">
                当前页面仅依据 protocol 目录字段聚合，不包含额外的 responder profile 文案。
              </p>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="border-2 border-white/20 bg-white/10 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-400">Hotlines</p>
                  <p className="mt-2 text-3xl font-black uppercase">{responder.hotline_count}</p>
                </div>
                <div className="border-2 border-white/20 bg-white/10 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-400">Capabilities</p>
                  <p className="mt-2 text-3xl font-black uppercase">{responder.capabilities.length}</p>
                </div>
              </div>

              <div className="mt-6 border-t-2 border-white/20 pt-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-400">能力范围</p>
                <p className="mt-3 text-sm font-bold leading-6 text-zinc-200">
                  {responder.capabilities.map(localizeCapability).join(" · ") || "未提供"}
                </p>
              </div>
            </article>
          </aside>

          <div className="min-w-0">
            <div className="border-4 border-black bg-white/85 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
              <div className="border-b-4 border-black bg-[#F8F6EF] px-6 py-5">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-zinc-500">Hotline 列表</p>
                <h3 className="mt-2 text-3xl font-black uppercase">这个 responder 的公开 hotlines</h3>
              </div>

              <div className="divide-y-4 divide-black">
                {(responder.hotlines || []).map((item) => (
                  <a
                    key={item.hotline_id}
                    href={buildMarketplacePath(item)}
                    className="block bg-white px-6 py-6 transition-colors hover:bg-[#FCFAF3]"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <p className="text-2xl font-black uppercase leading-none">{item.display_name}</p>
                          <span className="border-2 border-black bg-[#F8F6EF] px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.12em]">
                            hotline
                          </span>
                        </div>
                        <p className="mt-3 break-all text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{item.hotline_id}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="border-2 border-black bg-white px-3 py-2 text-xs font-black uppercase">{localizeStatus(item.status)}</span>
                        <span className="border-2 border-black bg-[#ECFCCB] px-3 py-2 text-xs font-black uppercase">
                          {localizeStatus(item.availability_status)}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      <div className="border-2 border-black bg-[#F8F6EF] px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">任务类型</p>
                        <p className="mt-2 text-xs font-black leading-5 text-zinc-800">
                          {(item.task_types || []).map(localizeTaskType).join(" · ") || "未提供"}
                        </p>
                      </div>
                      <div className="border-2 border-black bg-[#F8F6EF] px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">能力</p>
                        <p className="mt-2 text-xs font-black leading-5 text-zinc-800">
                          {(item.capabilities || []).slice(0, 3).map(localizeCapability).join(" · ") || "未提供"}
                        </p>
                      </div>
                      <div className="border-2 border-black bg-[#F8F6EF] px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">Template Ref</p>
                        <p className="mt-2 break-all text-xs font-black leading-5 text-zinc-800">{item.template_ref}</p>
                      </div>
                    </div>

                    <div className="mt-5 inline-flex items-center gap-2 text-sm font-black uppercase text-black">
                      查看 hotline 详情
                      <ArrowRight className="h-4 w-4" strokeWidth={3} />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HotlineView({
  hotline,
  templateBundle,
  relatedHotlines,
  loadingState
}: {
  hotline: MarketplaceHotline;
  templateBundle: MarketplaceTemplateBundle | null;
  relatedHotlines: MarketplaceHotline[];
  loadingState: "loading" | "ready" | "fallback";
}) {
  return (
    <section id="marketplace-hotline" className="scroll-mt-24 px-4 py-12 sm:px-6 lg:px-8 md:py-16">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <a
            href={buildResponderPath(hotline)}
            className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={3} />
            返回 responder 页面
          </a>
          {loadingState === "fallback" ? (
            <div className="inline-flex items-center gap-2 border-4 border-black bg-[#F8F6EF] px-4 py-3 text-sm font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
              Preview 示例详情，不代表已上线或可调用
            </div>
          ) : null}
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <article className="border-4 border-black bg-white/80 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b-4 border-black pb-5">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-zinc-500">{hotline.responder_id}</p>
                <h2 className="mt-2 text-4xl font-black uppercase leading-none">{hotline.display_name}</h2>
                <p className="mt-3 break-all text-xs font-black uppercase tracking-[0.14em] text-zinc-500">{hotline.hotline_id}</p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="border-2 border-black bg-white px-3 py-2 text-xs font-black uppercase">{localizeStatus(hotline.status)}</span>
                <span className="border-2 border-black bg-[#FEF3C7] px-3 py-2 text-xs font-black uppercase">
                  {localizeStatus(hotline.availability_status)}
                </span>
              </div>
            </div>

            <div className="mt-6 grid gap-5">
              {hotline.summary ? (
                <div className="border-2 border-black bg-[#111111] p-5 text-white">
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#A3E635]">一句话说明</p>
                  <p className="mt-3 text-base font-bold leading-7">{hotline.summary}</p>
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="border-2 border-black bg-[#F8F6EF] p-4">
                  <strong className="text-sm font-black uppercase">基础信息</strong>
                  <div className="mt-3 grid gap-2 text-sm font-bold text-zinc-800">
                    <p>Responder ID：{hotline.responder_id}</p>
                    <p>Template Ref：{hotline.template_ref}</p>
                    <p>版本：{hotline.version || "未提供"}</p>
                    <p>最近心跳：{formatUtcDate(hotline.last_heartbeat_at)}</p>
                  </div>
                </div>

                <div className="border-2 border-black bg-[#F8F6EF] p-4">
                  <div className="flex items-center gap-3">
                    <KeyRound className="h-5 w-5 text-black" strokeWidth={3} />
                    <strong className="text-sm font-black uppercase">验签材料</strong>
                  </div>
                  <p className="mt-3 text-sm font-bold text-zinc-800">{summarizePublicKey(hotline.responder_public_key_pem)}</p>
                </div>
              </div>

              {(hotline.recommended_for?.length || hotline.not_recommended_for?.length || hotline.limitations?.length) ? (
                <div className="border-2 border-black bg-[#F8F6EF] p-4">
                  <strong className="text-sm font-black uppercase">适用性评估</strong>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    {hotline.recommended_for?.length ? (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">适合场景</p>
                        <ul className="mt-2 space-y-1.5">
                          {hotline.recommended_for.map((item, i) => (
                            <li key={i} className="text-xs font-bold leading-5 text-zinc-800 before:mr-1.5 before:content-['✓']">{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {hotline.not_recommended_for?.length ? (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">不适合场景</p>
                        <ul className="mt-2 space-y-1.5">
                          {hotline.not_recommended_for.map((item, i) => (
                            <li key={i} className="text-xs font-bold leading-5 text-zinc-800 before:mr-1.5 before:content-['✗']">{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {hotline.limitations?.length ? (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">已知限制</p>
                        <ul className="mt-2 space-y-1.5">
                          {hotline.limitations.map((item, i) => (
                            <li key={i} className="text-xs font-bold leading-5 text-zinc-800 before:mr-1.5 before:content-['⚠']">{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {(hotline.input_summary || hotline.output_summary) ? (
                <div className="border-2 border-black bg-[#F8F6EF] p-4">
                  <strong className="text-sm font-black uppercase">输入/输出说明</strong>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    {hotline.input_summary ? (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">输入</p>
                        <p className="mt-2 text-sm font-bold leading-6 text-zinc-800">{hotline.input_summary}</p>
                        {hotline.input_attachments?.accepts_files ? (
                          <div className="mt-3">
                            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">文件附件角色</p>
                            <div className="mt-2 space-y-2">
                              {hotline.input_attachments.file_roles?.map((role) => (
                                <div key={role.role} className="border-l-4 border-[#3B82F6] pl-3">
                                  <p className="text-xs font-black uppercase">{role.role}{role.required ? <span className="ml-1 text-[10px] text-red-600">必需</span> : <span className="ml-1 text-[10px] text-zinc-500">可选</span>}</p>
                                  {role.description ? <p className="mt-0.5 text-xs font-bold text-zinc-600">{role.description}</p> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {hotline.output_summary ? (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-500">输出</p>
                        <p className="mt-2 text-sm font-bold leading-6 text-zinc-800">{hotline.output_summary}</p>
                        {hotline.output_attachments?.includes_files ? (
                          <div className="mt-3">
                            <p className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">文件输出角色</p>
                            <div className="mt-2 space-y-2">
                              {hotline.output_attachments.file_roles?.map((role) => (
                                <div key={role.role} className="border-l-4 border-[#8B5CF6] pl-3">
                                  <p className="text-xs font-black uppercase">{role.role}{role.guaranteed ? <span className="ml-1 text-[10px] text-green-700">必定返回</span> : <span className="ml-1 text-[10px] text-zinc-500">视情况</span>}</p>
                                  {role.description ? <p className="mt-0.5 text-xs font-bold text-zinc-600">{role.description}</p> : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="border-2 border-black bg-[#F8F6EF] p-4">
                <div className="flex items-center gap-3">
                  <RadioTower className="h-5 w-5 text-black" strokeWidth={3} />
                  <strong className="text-sm font-black uppercase">任务类型与能力</strong>
                </div>
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">任务类型</p>
                    <ul className="mt-2 space-y-2 text-sm font-bold text-zinc-800">
                      {(hotline.task_types || []).length > 0 ? (
                        (hotline.task_types || []).map((taskType) => (
                          <li key={taskType} className="flex gap-2">
                            <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-black" strokeWidth={3} />
                            <span>{localizeTaskType(taskType)}</span>
                          </li>
                        ))
                      ) : (
                        <li>未提供</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">能力</p>
                    <p className="mt-2 text-sm font-bold leading-6 text-zinc-800">
                      {(hotline.capabilities || []).map(localizeCapability).join(" · ") || "未提供"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-black bg-[#F8F6EF] p-4">
                <div className="flex items-center gap-3">
                  <LibraryBig className="h-5 w-5 text-black" strokeWidth={3} />
                  <strong className="text-sm font-black uppercase">执行提示</strong>
                </div>
                <div className="mt-3 grid gap-4 md:grid-cols-2 text-sm font-bold text-zinc-800">
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">SLA Hint</p>
                    <p className="mt-2">P95 执行：{formatExecMs(hotline.sla_hint?.p95_exec_ms)}</p>
                    <p className="mt-1">
                      7 日超时率：
                      {typeof hotline.sla_hint?.timeout_rate_7d === "number" ? `${Math.round(hotline.sla_hint.timeout_rate_7d * 100)}%` : "未提供"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-500">ETA Hint</p>
                    <p className="mt-2">排队 P50：{hotline.eta_hint?.queue_p50_s ? `${hotline.eta_hint.queue_p50_s}s` : "未提供"}</p>
                    <p className="mt-1">执行 P50：{hotline.eta_hint?.exec_p50_s ? `${hotline.eta_hint.exec_p50_s}s` : "未提供"}</p>
                    <p className="mt-1">执行 P95：{hotline.eta_hint?.exec_p95_s ? `${hotline.eta_hint.exec_p95_s}s` : "未提供"}</p>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <div className="grid gap-6">
            <article className="border-4 border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.24em] text-[#A3E635]">Template Bundle</p>
              <h3 className="mt-3 text-2xl font-black uppercase">使用方式 & 合约模板</h3>
              <div className="mt-5 grid gap-4">
                <div className="border-2 border-white/30 bg-white/10 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-300">Input Schema</p>
                  <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words text-xs font-bold leading-6 text-zinc-100">
                    {jsonPreview(templateBundle?.input_schema)}
                  </pre>
                </div>

                {templateBundle?.input_attachments && (templateBundle.input_attachments as {accepts_files?: boolean}).accepts_files ? (
                  <div className="border-2 border-[#3B82F6]/60 bg-[#3B82F6]/10 p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#93C5FD]">Input Attachments</p>
                    <div className="mt-3 space-y-3">
                      {((templateBundle.input_attachments as {file_roles?: Array<{role:string;required?:boolean;description?:string;accepted_types?:string[]}>}).file_roles || []).map((role) => (
                        <div key={role.role} className="border-l-4 border-[#3B82F6] pl-3">
                          <p className="text-xs font-black uppercase text-white">{role.role} {role.required ? "· 必需" : "· 可选"}</p>
                          {role.description ? <p className="mt-0.5 text-xs font-bold text-zinc-300">{role.description}</p> : null}
                          {role.accepted_types?.length ? <p className="mt-0.5 text-[10px] font-bold text-zinc-400">{role.accepted_types.join(", ")}</p> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="border-2 border-white/30 bg-white/10 p-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-300">Output Schema</p>
                  <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words text-xs font-bold leading-6 text-zinc-100">
                    {jsonPreview(templateBundle?.output_schema)}
                  </pre>
                </div>

                {templateBundle?.output_attachments && (templateBundle.output_attachments as {includes_files?: boolean}).includes_files ? (
                  <div className="border-2 border-[#8B5CF6]/60 bg-[#8B5CF6]/10 p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#C4B5FD]">Output Attachments</p>
                    <div className="mt-3 space-y-3">
                      {((templateBundle.output_attachments as {file_roles?: Array<{role:string;guaranteed?:boolean;description?:string;possible_types?:string[]}>}).file_roles || []).map((role) => (
                        <div key={role.role} className="border-l-4 border-[#8B5CF6] pl-3">
                          <p className="text-xs font-black uppercase text-white">{role.role} {role.guaranteed ? "· 必定返回" : "· 视情况"}</p>
                          {role.description ? <p className="mt-0.5 text-xs font-bold text-zinc-300">{role.description}</p> : null}
                          {role.possible_types?.length ? <p className="mt-0.5 text-[10px] font-bold text-zinc-400">{role.possible_types.join(", ")}</p> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {templateBundle?.input_examples?.length ? (
                  <div className="border-2 border-white/30 bg-white/10 p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-300">输入示例</p>
                    <div className="mt-3 space-y-4">
                      {templateBundle.input_examples.map((example, i) => (
                        <div key={i} className="border-t border-white/20 pt-3 first:border-0 first:pt-0">
                          {example.title ? <p className="text-xs font-black uppercase text-[#A3E635]">{example.title}</p> : null}
                          {example.description ? <p className="mt-1 text-xs font-bold text-zinc-300">{example.description}</p> : null}
                          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs font-bold leading-5 text-zinc-100">
                            {jsonPreview(example.params)}
                          </pre>
                          {example.attachments?.length ? (
                            <p className="mt-2 text-[10px] font-bold text-zinc-400">附件：{example.attachments.map((a) => `${a.role} (${a.mime_type || "未知类型"})`).join(", ")}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {templateBundle?.output_examples?.length ? (
                  <div className="border-2 border-white/30 bg-white/10 p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-300">输出示例</p>
                    <div className="mt-3 space-y-4">
                      {templateBundle.output_examples.map((example, i) => (
                        <div key={i} className="border-t border-white/20 pt-3 first:border-0 first:pt-0">
                          {example.title ? <p className="text-xs font-black uppercase text-[#F97316]">{example.title}</p> : null}
                          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs font-bold leading-5 text-zinc-100">
                            {jsonPreview(example.result)}
                          </pre>
                          {example.attachments?.length ? (
                            <p className="mt-2 text-[10px] font-bold text-zinc-400">附件：{example.attachments.map((a) => `${a.role} (${a.mime_type || "未知类型"})`).join(", ")}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {templateBundle?.readme_markdown ? (
                  <div className="border-2 border-white/30 bg-white/10 p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-300">README</p>
                    <p className="mt-3 text-sm font-bold leading-6 text-zinc-100">{templateBundle.readme_markdown}</p>
                  </div>
                ) : null}
              </div>
            </article>

            {relatedHotlines.length > 0 ? (
              <article className="border-4 border-black bg-white/80 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-zinc-500">同 responder 下的其他 hotlines</p>
                <div className="mt-4 grid gap-3">
                  {relatedHotlines.map((item) => (
                    <a
                      key={item.hotline_id}
                      href={buildMarketplacePath(item)}
                      className="border-2 border-black bg-[#F8F6EF] p-4 transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                    >
                      <p className="text-sm font-black uppercase">{item.display_name}</p>
                      <p className="mt-2 break-all text-[11px] font-black uppercase tracking-[0.12em] text-zinc-600">{item.hotline_id}</p>
                    </a>
                  ))}
                </div>
              </article>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function NotFoundView() {
  return (
    <Section
      id="marketplace-not-found"
      eyebrow="未找到"
      title="没有找到对应的目录条目"
      description="你请求的 responder 或 hotline 当前不在公开目录中，或者已经不再可见。"
    >
      <a
        href="/marketplace"
        className="inline-flex items-center gap-2 border-4 border-black bg-[#A3E635] px-5 py-3 text-sm font-black uppercase text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={3} />
        返回 Marketplace
      </a>
    </Section>
  );
}

export default function Marketplace() {
  const location = useLocation();
  const route = useMemo(() => parseMarketplaceRoute(location.pathname), [location.pathname]);
  const [items, setItems] = useState<MarketplaceHotline[]>(FALLBACK_HOTLINES.map(normalizeHotline));
  const [loadingState, setLoadingState] = useState<"loading" | "ready" | "fallback">("loading");
  const [hotlineDetailState, setHotlineDetailState] = useState<{ hotlineId: string; detail: MarketplaceHotline | null } | null>(null);
  const [templateBundleState, setTemplateBundleState] = useState<{ hotlineId: string; bundle: MarketplaceTemplateBundle | null } | null>(null);

  useEffect(() => {
    let active = true;
    requestHotlineCatalog()
      .then((payload) => {
        if (!active || !Array.isArray(payload.items)) {
          return;
        }
        if (payload.items.length === 0) {
          setItems([]);
          setLoadingState("ready");
          return;
        }
        setItems(payload.items.map(normalizeHotline));
        setLoadingState("ready");
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setLoadingState("fallback");
      });
    return () => {
      active = false;
    };
  }, []);

  const responders = useMemo(() => uniqueResponders(items), [items]);
  const responderProfile =
    route.kind === "responder" || route.kind === "hotline"
      ? responders.find((item) => (item.responder_slug || slugifyMarketplaceSegment(item.responder_id)) === route.responderSlug) || null
      : null;

  const matchedHotline =
    route.kind === "hotline"
      ? items.find(
          (item) =>
            (item.responder_slug || slugifyMarketplaceSegment(item.responder_id)) === route.responderSlug &&
            (item.hotline_slug || slugifyMarketplaceSegment(item.display_name || item.hotline_id)) === route.hotlineSlug
        ) || null
      : null;

  const relatedHotlines = useMemo(() => {
    if (!matchedHotline) {
      return [];
    }
    return items.filter((item) => item.responder_id === matchedHotline.responder_id && item.hotline_id !== matchedHotline.hotline_id).slice(0, 3);
  }, [items, matchedHotline]);

  const hotlineDetail =
    route.kind === "hotline" && matchedHotline
      ? hotlineDetailState?.hotlineId === matchedHotline.hotline_id
        ? hotlineDetailState.detail
        : matchedHotline
      : null;

  const templateBundle =
    route.kind === "hotline" && matchedHotline && templateBundleState?.hotlineId === matchedHotline.hotline_id
      ? templateBundleState.bundle
      : null;

  useEffect(() => {
    if (route.kind !== "hotline" || !matchedHotline) {
      return;
    }
    let active = true;
    requestHotlineDetail(matchedHotline.hotline_id)
      .then((payload) => {
        if (!active) {
          return;
        }
        setHotlineDetailState({ hotlineId: matchedHotline.hotline_id, detail: normalizeHotline(payload) });
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setHotlineDetailState({ hotlineId: matchedHotline.hotline_id, detail: matchedHotline });
      });
    return () => {
      active = false;
    };
  }, [matchedHotline, route]);

  useEffect(() => {
    if (route.kind !== "hotline" || !matchedHotline) {
      return;
    }
    let active = true;
    requestHotlineTemplateBundle(matchedHotline.hotline_id, matchedHotline.template_ref)
      .then((payload) => {
        if (!active) {
          return;
        }
        setTemplateBundleState({ hotlineId: matchedHotline.hotline_id, bundle: payload });
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setTemplateBundleState({
          hotlineId: matchedHotline.hotline_id,
          bundle: FALLBACK_TEMPLATE_BUNDLES[matchedHotline.hotline_id] || null,
        });
      });
    return () => {
      active = false;
    };
  }, [matchedHotline, route]);

  let content;
  if (route.kind === "index") {
    content = <HotlineListView items={items} responders={responders} loadingState={loadingState} />;
  } else if (route.kind === "responder") {
    content = responderProfile ? <ResponderView responder={responderProfile} /> : <NotFoundView />;
  } else {
    content = hotlineDetail ? (
      <HotlineView hotline={hotlineDetail} templateBundle={templateBundle} relatedHotlines={relatedHotlines} loadingState={loadingState} />
    ) : (
      <NotFoundView />
    );
  }

  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />
      <main className="relative z-10 w-full overflow-hidden pb-10">{content}</main>
      <BackCoverFooter />
    </PageShell>
  );
}
